import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { ConfigurationSchema } from 'frontastic-common';

const formatProductColour = (colourName) => {
  if (!colourName) {
    return '';
  }

  const cleanedName = colourName.replace(/[^a-zA-Z0-9- ]/g, '');
  return cleanedName.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

export const ProductSchema = (props) => {
  const { product, node } = props;

  const context = useSelector((state) => state.app.context);

  const projectSchema = new ConfigurationSchema(
    context.projectConfigurationSchema,
    context.projectConfiguration,
  );

  const removeHtmlTags = (str) => str.replace(/<\/?[^>]+(>|$)/g, '');

  const pageTitleSuffixSchema = projectSchema.get('frontasticPageTitleSuffix');
  const pageTitleSuffix =
    pageTitleSuffixSchema?.[context.locale] ||
    pageTitleSuffixSchema?.['en_AU@AUD'] ||
    pageTitleSuffixSchema;

  const pageFallbackDescriptionSuffix = projectSchema.get(
    'pageFallbackDescriptionSuffix',
  )?.[context.locale];

  const pageTitleIsSetOnNode =
    node.configuration?.seoTitle?.length > 0 && typeof node.configuration?.seoTitle !== 'undefined';
  const pageDescriptionIsSetOnNode =
    node.configuration?.seoDescription?.length > 0 && typeof node.configuration?.seoDescription !== 'undefined';

  const storeUrl = context.projectConfiguration['EXTENSION_PUBLIC_URL'];

  const offers = product.variants?.map((variant) => {
    const discount = variant.discountedPrice / 100;
    return {
      '@type': 'Offer',
      priceCurrency: variant.currency,
      price: discount && discount !== 0 ? discount : variant.price / 100,
      url: `${storeUrl}${product._url}`,
      availability: variant.isOnStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    };
  });

  const maxPrice = offers.reduce((max, offer) => Math.max(max, offer.price), 0);
  const minPrice = offers.reduce(
    (min, offer) => Math.min(min, offer.price),
    maxPrice,
  );

  const imagesArray = product.variants?.[0]?.images?.map((x) => x.url) || [];
  const currentProductColour = product?.projectSpecificData?.attributes?.find((item) => item?.name === 'colour')?.value;
  const productColour = formatProductColour(currentProductColour);
  const productUrl = `${storeUrl}${product._url}`;
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    '@id': productUrl,
    sku: product.sku || product.slug,
    url: productUrl,
    name: product.name,
    description: removeHtmlTags(product.description),
    image: imagesArray,
    brand: { '@type': 'Brand', name: context?.project?.name },
    offers: {
      '@type': 'AggregateOffer',
      highPrice: maxPrice,
      lowPrice: minPrice,
      offerCount: offers.length,
      priceCurrency: product.variants?.[0]?.currency,
      offers: offers,
    },
  };

  const countryName =
    context.locale === 'en_AU@NZD' ? 'New Zealand' : 'Australia';
  const metaTitle = `${product.name} ${productColour ? `in ${productColour}` : ''}${pageTitleSuffix}`;
  const metaDescription = `Shop ${
    product.name
  } ${productColour} online in ${countryName} at ${context.project.name.toUpperCase()}.${
    pageFallbackDescriptionSuffix ? ` ${pageFallbackDescriptionSuffix}` : ''
  }`;

  useEffect(() => {
    const title = document.querySelector('title');
    if (title && node?.name) {
      title.innerText = pageTitleIsSetOnNode ? `${node.configuration?.seoTitle}${pageTitleSuffix}` : metaTitle;
    }
  }, [node, metaTitle]);

  return (
    <>
      <Helmet>
        {!pageTitleIsSetOnNode && <title>{metaTitle}</title>}
        {!pageDescriptionIsSetOnNode && (
          <meta name="description" content={metaDescription} />
        )}

        <meta property="og:url" content={storeUrl + product._url} />
        <meta
          property="og:image"
          content={product.variants?.[0].images?.[0]?.url}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content={
            projectSchema.get('schemaOrgName')?.[context.locale] ||
            projectSchema.get('schemaOrgName')
          }
        />
        <meta property="og:locale" content="en-AU" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:country-name" content="au" />
      </Helmet>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </>
  );
};
